/**
 * Запуск генерации кадра (прежняя `.gen-panel`).
 *
 * Пресет — одна ручка вместо трёх, и он же несёт приём экономии: сначала
 * пачка дешёвых превью, потом пересъёмка выбранного в качестве (⤴ на плитке).
 * Цена — рядом с кнопкой: решение «жать или нет» принимается здесь.
 * Ручной режим спрятан за «подробнее»: три селекта в ленте из десяти кадров —
 * это шум.
 *
 * Ключ идемпотентности — в `useGenerate`: панель создаётся при открытии и
 * этим же получает свежий ключ.
 */

import clsx from "clsx";
import { Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { mediaUrl } from "@/lib/media";
import type { Candidate, GenOptions } from "@/lib/project";
import { groupInOrder, useGenerate, usd } from "@/lib/project";

import { Btn, Empty, Hint, selectClass } from "./ui";

const MANUAL = "";
const COUNTS = [1, 2, 4];

export function GenPanel({
  pid,
  sid,
  inputs,
  options,
}: {
  pid: number;
  sid: number;
  inputs: Candidate[];
  options: GenOptions;
}) {
  const { run, busy } = useGenerate(pid, sid);
  const [preset, setPreset] = useState(options.default_preset);
  const [model, setModel] = useState(options.defaults.model);
  const [quality, setQuality] = useState(options.defaults.quality);
  const [size, setSize] = useState(options.defaults.size);
  const [count, setCount] = useState(1);

  // Входы по умолчанию — как решил сервер (`checked`). Когда набор кандидатов
  // меняется (загрузили фото, отметили удачный кадр), выбор пересобирается:
  // держать галки от старого набора значило бы слать не то, что видно.
  const signature = inputs.map((c) => `${c.asset_id}:${c.checked ? 1 : 0}`).join(",");
  const initial = useMemo(
    () => new Set(inputs.filter((c) => c.checked).map((c) => c.asset_id)),
    [signature],
  );
  const [chosen, setChosen] = useState<Set<number>>(initial);
  useEffect(() => setChosen(initial), [initial]);

  const sizes = options.models[model]?.sizes ?? [];
  const groups = groupInOrder(inputs, (c) => c.group);

  // Поменял ручное поле — значит, хочет ручной режим: пресет иначе молча
  // перебил бы выбор (сервер берёт пресет, если он задан).
  const manual = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPreset(MANUAL);
  };

  const toggle = (id: number) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () =>
    run({
      assets: inputs.filter((c) => chosen.has(c.asset_id)).map((c) => c.asset_id),
      preset,
      model,
      quality,
      size: sizes.includes(size) ? size : (sizes[0] ?? size),
      count,
    });

  return (
    <div className="grid gap-3 rounded-[var(--radius-ui)] border border-border bg-bg p-3" data-annot="запуск генерации">
      <div className="flex flex-wrap items-stretch gap-2">
        {Object.entries(options.presets).map(([code, p]) => (
          <label
            key={code}
            title={p.note}
            className={clsx(
              "flex cursor-pointer flex-col rounded-[var(--radius-ui)] border px-2.5 py-1.5",
              preset === code ? "border-accent bg-accent-dim" : "border-border hover:bg-bg-hover",
            )}
          >
            <span className="flex items-center gap-1.5">
              <input
                type="radio"
                name={`preset-${sid}`}
                checked={preset === code}
                onChange={() => setPreset(code)}
                className="accent-[var(--accent)]"
              />
              <span className={clsx("text-[12.5px]", preset === code && "font-medium text-accent-text")}>{p.label}</span>
            </span>
            <em className="tnum text-[11px] text-fg-faint not-italic">
              {p.size}
              {p.n > 1 ? ` × ${p.n}` : ""} · {usd(options.preset_costs[code])}
            </em>
          </label>
        ))}
        <label
          title="модель, качество, размер и количество — вручную, в «подробнее»"
          className={clsx(
            "flex cursor-pointer flex-col justify-center rounded-[var(--radius-ui)] border px-2.5 py-1.5",
            preset === MANUAL ? "border-accent bg-accent-dim" : "border-border hover:bg-bg-hover",
          )}
        >
          <span className="flex items-center gap-1.5">
            <input
              type="radio"
              name={`preset-${sid}`}
              checked={preset === MANUAL}
              onChange={() => setPreset(MANUAL)}
              className="accent-[var(--accent)]"
            />
            <span className={clsx("text-[12.5px]", preset === MANUAL && "font-medium text-accent-text")}>Вручную</span>
          </span>
          <em className="text-[11px] text-fg-faint not-italic">цена — оценкой в ленте</em>
        </label>
        <Btn tone="primary" className="ml-auto self-center" disabled={busy} onClick={submit}>
          <Sparkles size={13} />
          {busy ? "ставлю в очередь…" : "Сгенерировать"}
        </Btn>
      </div>

      <details open={preset === MANUAL} className="group">
        <summary className="cursor-pointer text-[12px] text-fg-muted select-none hover:text-fg">
          подробнее: модель, качество, размер
        </summary>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select value={model} onChange={(e) => manual(setModel)(e.target.value)} className={selectClass}>
            {Object.entries(options.models).map(([code, m]) => (
              <option key={code} value={code}>
                {m.label} — {m.note}
              </option>
            ))}
          </select>
          <select value={quality} onChange={(e) => manual(setQuality)(e.target.value)} className={selectClass}>
            {options.qualities.map(([q, name]) => (
              <option key={q} value={q}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={sizes.includes(size) ? size : (sizes[0] ?? "")}
            onChange={(e) => manual(setSize)(e.target.value)}
            className={selectClass}
          >
            {sizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={count} onChange={(e) => manual(setCount)(Number(e.target.value))} className={selectClass}>
            {COUNTS.map((n) => (
              <option key={n} value={n}>
                {n} шт
              </option>
            ))}
          </select>
          <Hint>ручной режим работает, когда выбрано «Вручную» — правка поля включает его сама</Hint>
        </div>
      </details>

      <div className="grid gap-2">
        {groups.length === 0 ? (
          <Empty>Отправлять нечего: ни референсов проекта, ни фото в слотах кадра.</Empty>
        ) : (
          groups.map(([group, items]) => (
            <div key={group}>
              <Hint>{group}</Hint>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {items.map((c) => {
                  const on = chosen.has(c.asset_id);
                  return (
                    <label
                      key={`${group}-${c.asset_id}`}
                      title={c.hint ?? c.label}
                      className="w-[64px] cursor-pointer text-[10.5px]"
                    >
                      <input type="checkbox" checked={on} onChange={() => toggle(c.asset_id)} className="sr-only" />
                      <img
                        src={mediaUrl(c.asset_path)}
                        alt={c.label}
                        loading="lazy"
                        className={clsx(
                          "aspect-[3/4] w-full rounded-[4px] border-2 object-cover transition-opacity",
                          on ? "border-accent opacity-100" : "border-transparent opacity-40",
                        )}
                      />
                      <span className={clsx("block truncate", on ? "text-accent-text" : "text-fg-faint")}>
                        {c.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
